import type React from "react";
import { createContext, useContext, useState, useEffect } from "react";

export type RoasterProfileType = "BLP" | "SS" | "BC OG";

interface RoasterProfileContextType {
  profileType: RoasterProfileType;
  setProfileType: (type: RoasterProfileType) => void;
  isLoaded: boolean;
}

export const RoasterProfileContext = createContext<
  RoasterProfileContextType | undefined
>(undefined);

// Sample data for Storybook stories
export const sampleRoasterProfileContext: RoasterProfileContextType = {
  profileType: "BLP",
  setProfileType: () => {},
  isLoaded: true,
};

export const sampleBCOGRoasterProfileContext: RoasterProfileContextType = {
  profileType: "BC OG",
  setProfileType: () => {},
  isLoaded: true,
};

export function RoasterProfileProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [profileType, setProfileTypeState] =
    useState<RoasterProfileType>("BLP");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const savedProfile = localStorage.getItem(
      "roasterProfileType",
    ) as RoasterProfileType;
    if (
      savedProfile === "BLP" ||
      savedProfile === "SS" ||
      savedProfile === "BC OG"
    ) {
      setProfileTypeState(savedProfile);
    }
    setIsLoaded(true);
  }, []);

  const setProfileType = (type: RoasterProfileType) => {
    setProfileTypeState(type);
    localStorage.setItem("roasterProfileType", type);
  };

  return (
    <RoasterProfileContext.Provider
      value={{ profileType, setProfileType, isLoaded }}
    >
      {children}
    </RoasterProfileContext.Provider>
  );
}

export function useRoasterProfile() {
  const context = useContext(RoasterProfileContext);
  if (context === undefined) {
    throw new Error(
      "useRoasterProfile must be used within a RoasterProfileProvider",
    );
  }
  return context;
}
